import { Button } from "@/components/ui/button";
import { Link, Navigate } from "react-router-dom";
import {
  DespachoMockBanner,
  DespachoPageShell,
  DespachoSectionCard,
} from "@/components/despacho/DespachoUi";
import { DESPACHO_FASE2_LICENSE_FIELDS, DESPACHO_FASE2_TODOS } from "@/modules/despacho/despachoFase2Todos";
import { usePlatformDesigner } from "@/lib/usePlatformDesigner";
import { DESPACHO_INICIO_PATH } from "@/modules/despacho/despachoService";

const SECCIONES = [
  "Roles y permisos del módulo (por espacio de trabajo del comprador)",
  "Tipos de tarea y plantillas recurrentes",
  "Recordatorios y notificaciones",
  "Importación de clientes operativos (Excel)",
  "Centro de mensajes — quién ve canal BNT vs equipo interno",
  "Integraciones opcionales (Visor fiscal, Laboral, Trámites)",
];

const ACCESOS_AUXILIARES = [
  "El dueño del despacho comprador invita usuarios auxiliares del comprador desde Panel General → Actividades de Equipo.",
  "Captura nombre, correo, rol, clientes operativos asignados (opcional) y permisos iniciales.",
  "El sistema envía invitación; el auxiliar acepta y crea su propia contraseña.",
  "El dueño no ve, no captura ni guarda la contraseña del auxiliar.",
  "El dueño puede activar/desactivar, cambiar rol, asignar clientes y tareas, enviar recuperación o revocar acceso.",
  "El auxiliar solo ve clientes operativos, tareas y chats asignados; no accede a otro espacio de trabajo ni a otro comprador.",
];

export default function ConfiguracionDespacho() {
  const isPlatformDesigner = usePlatformDesigner();
  if (!isPlatformDesigner) {
    return <Navigate to={DESPACHO_INICIO_PATH} replace />;
  }

  return (
    <DespachoPageShell
      title="Configuración"
      subtitle="Parámetros del módulo para el dueño del despacho comprador (no confundir con comprador/distribuidor BNT)."
      testId="despacho-configuracion"
    >
      <DespachoMockBanner />

      <div className="rounded-sm border border-[#0F2D5C]/15 bg-[#0F2D5C]/[0.04] px-4 py-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-[#0F2D5C] m-0">Resumen comercial del comprador</p>
          <p className="text-xs text-slate-600 m-0 mt-1">
            Vista de diseño con módulos, vigencias y límites (mock + entitlements de solo lectura).
          </p>
        </div>
        <Button asChild variant="outline" size="sm" className="rounded-sm">
          <Link to="/despacho/comprador" data-testid="link-despacho-comprador-overview">
            Abrir «Comprador / despacho»
          </Link>
        </Button>
      </div>

      <DespachoSectionCard
        title="Límites comerciales de la licencia (demostración)"
        description="Cupo definido por el diseñador/distribuidor al emitir o administrar la licencia del comprador."
      >
        <ul className="text-sm text-slate-700 space-y-2 m-0 list-disc pl-4 marker:text-[#3DA873]">
          <li>
            El límite de <strong>auxiliares autorizados</strong> pertenece a la licencia del comprador/despacho, no
            a cada cliente operativo ni al cliente fiscal del Visor SAT.
          </li>
          <li>
            El límite de auxiliares será definido por el diseñador/distribuidor desde la administración de
            compradores/licencias.
          </li>
          <li>Este límite no se implementa todavía en Fase 1; será validado en backend durante Fase 2.</li>
          <li>
            Ejemplos de plan mock: básico (hasta 5 auxiliares), plus (hasta 10), avanzado (límite personalizado por
            contrato).
          </li>
          <li>
            Si el despacho necesita más auxiliares, debe solicitar ampliación a Soporte BNT o cambio de plan.
          </li>
        </ul>
        <p className="text-xs text-slate-500 m-0 mt-3">
          Campos futuros sugeridos en licencia: {DESPACHO_FASE2_LICENSE_FIELDS.join(", ")}.
        </p>
      </DespachoSectionCard>

      <DespachoSectionCard
        title="Accesos de auxiliares (demostración)"
        description="Usuarios internos del despacho — distintos del comprador de la licencia y del cliente fiscal del Visor."
      >
        <ul className="text-sm text-slate-700 space-y-2 m-0 list-disc pl-4 marker:text-[#3DA873]">
          {ACCESOS_AUXILIARES.map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ul>
      </DespachoSectionCard>

      <DespachoSectionCard title="Secciones planificadas">
        <ul className="text-sm text-slate-700 space-y-2 m-0 list-disc pl-4 marker:text-[#3DA873]">
          {SECCIONES.map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ul>
        <div className="pt-4 flex flex-wrap gap-2">
          <Button type="button" variant="outline" className="rounded-sm" disabled>
            Guardar (mock)
          </Button>
        </div>
      </DespachoSectionCard>

      <DespachoSectionCard
        title="TODOs Fase 2 (dominio y permisos)"
        description="Reglas de arquitectura antes de implementar backend."
      >
        <ul className="text-xs text-slate-600 space-y-1.5 m-0 list-disc pl-4 marker:text-[#3DA873]">
          {DESPACHO_FASE2_TODOS.map((todo) => (
            <li key={todo}>{todo}</li>
          ))}
        </ul>
      </DespachoSectionCard>
    </DespachoPageShell>
  );
}
